// core/stores/presenceStore.ts — live online-by-userId lookup from realtime `presence` events.
//
// ConversationSerializer already sends a per-DM `online` boolean recomputed
// server-side on every page load/refetch, so this store only needs to cover
// the gap: a LIVE flip while a page is open, without a refetch. It does NOT
// (and structurally cannot, from the client alone) map straight onto a
// Conversation — ConversationSerializer never sends the other participant's
// raw numeric id (only name/av/online), and Conversation carries no
// `otherUserId` field. See the realtime feature report for the honest
// accounting of that gap. Any caller that DOES have the other participant's
// numeric id (e.g. via a resolved user record) can use this as a live
// override on top of the initially-hydrated `conversation.online`.

import { create } from 'zustand';

interface PresenceState {
  online: Record<number, boolean>;
  setOnline: (userId: number, online: boolean) => void;
}

export const usePresenceStore = create<PresenceState>()((set) => ({
  online: {},
  setOnline: (userId, online) =>
    set((s) => ({ online: { ...s.online, [userId]: online } })),
}));

/** Live online override for a known user id, or undefined if no presence event has arrived. */
export function useOnlineOverride(userId: number | undefined): boolean | undefined {
  return usePresenceStore((s) => (userId === undefined ? undefined : s.online[userId]));
}
